import React from 'react';
import BlockContainer from '../BlockComponents/BlockContainer';
import BlockContentContainer from '../BlockComponents/BlockContentContainer';
import { ICountdownTimerBlockPreviewProps } from './ICountdownTimerBlockPreviewProps';
import styled from 'styled-components';
import { isValidHexColor } from 'components/PageBuilder/PageBuilderSideBar/BlockSettings/domain';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CountdownTimerType, getPaymentBlockData, PaymentBlockPurpose, UpsellUrgencyType } from 'containers/PageBuilder/SinglePageContainer/domain';

dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);

const BACKEND_DATE_FORMATTING: string = "YYYY-MM-DD HH:mm:00";

const DisplayDateContainer: any = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const DisplayDateUnitContainer: any = styled.div`
    width: fit-content;
    display: flex;
    flex-direction: column;
    align-items: center;

    &.mobile {
        transform: scale(.8);
        transform-origin: top center;
    }
`;

const DisplayDateText: any = styled.div<{design: any}>`
    margin: 0;
    padding: 0;
    font-weight: 700;

    font-size: ${(props: any) =>
    props.design.date_h1_font_size &&
    props.design.date_h1_font_size <= 80 ?
    `${props.design.date_h1_font_size}px` :
    `35px`
    };
    line-height: ${(props: any) =>
    props.design.date_h1_line_height &&
    props.design.date_h1_line_height <= 80 ?
        `${props.design.date_h1_line_height}px` :
        `45px`
    };
    font-family: ${(props: any) =>
    props.design.date_h1_font_family ?
        `${props.design.date_h1_font_family} !important` :
        `-apple-system, Arial !important`
    };
    color: ${(props: any) =>
    props.design.date_h1_color &&
    isValidHexColor(props.design.date_h1_color) ?
    props.design.date_h1_color :
    props.design.date_color && 
    isValidHexColor(props.design.date_color) ?
    props.design.date_color :
    '#333333'
    };
`;

const DisplayDateLabel: any = styled.p<{design: any}>`
    margin: 0;
    padding: 0;
    margin-top: 10px
    font-weight: 700;

    font-size: ${(props: any) =>
    props.design.date_label_p_font_size &&
    props.design.date_label_p_font_size <= 80 ?
    `${props.design.date_label_p_font_size}px` :
    `20px`
    };
    line-height: ${(props: any) =>
    props.design.date_label_p_line_height &&
    props.design.date_label_p_line_height <= 80 ?
        `${props.design.date_label_p_line_height}px` :
        `25px`
    };
    font-family: ${(props: any) =>
    props.design.date_label_p_font_family ?
        `${props.design.date_label_p_font_family} !important` :
        `-apple-system, Arial !important`
    };
    color: ${(props: any) =>
    props.design.date_label_p_color &&
    isValidHexColor(props.design.date_label_p_color) ?
    props.design.date_label_p_color :
    props.design.date_label_color && 
    isValidHexColor(props.design.date_label_color) ?
    props.design.date_label_color :
    '#333333'
    };
`;

const MILLISECONDS_IN_A_SECOND = 1000;
const SECONDS_IN_A_MINUTE: number = 60;

const CountdownTimerBlockPreview: React.FC<ICountdownTimerBlockPreviewProps> = (props) => {

    const [seconds, setSeconds] = React.useState(() => {
        if(
            props.blockData.timer_type === CountdownTimerType.Upsell &&
            getPaymentBlockData(props.pageBlocks) &&
            getPaymentBlockData(props.pageBlocks).upsell_urgency_type === UpsellUrgencyType.Timer &&
            getPaymentBlockData(props.pageBlocks).upsell_urgency_value
        ) {
            return getPaymentBlockData(props.pageBlocks).upsell_urgency_value * SECONDS_IN_A_MINUTE;
        }

        return 0;
    });

    const [displayDate, setDisplayDate] = React.useState<any>({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    });

    React.useEffect(() => {
        if(
            props.blockData.timer_type !== CountdownTimerType.Upsell
        ) {
            const timerInterval: any = setInterval(() => {
                if(isFutureDate(props.blockData.date ? props.blockData.date : dayjs.utc().format(BACKEND_DATE_FORMATTING))) {
                    const nextDisplayDate = timeToFutureDate(props.blockData.date ? props.blockData.date : dayjs.utc().format(BACKEND_DATE_FORMATTING));
                    setDisplayDate(() => nextDisplayDate);
                }
            }, MILLISECONDS_IN_A_SECOND);
    
            return () => clearInterval(timerInterval);
        }
    }, [props.blockData.date, props.blockData.timer_type]);

    React.useEffect(() => {
        if(
            props.blockData.timer_type === CountdownTimerType.Upsell &&
            getPaymentBlockData(props.pageBlocks) &&
            getPaymentBlockData(props.pageBlocks).upsell_urgency_type === UpsellUrgencyType.Timer &&
            getPaymentBlockData(props.pageBlocks).upsell_urgency_value
        ) {
            setDisplayDate({
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
            });
            setSeconds(getPaymentBlockData(props.pageBlocks).upsell_urgency_value * SECONDS_IN_A_MINUTE);
        }

        if(!(
            getPaymentBlockData(props.pageBlocks) && 
            getPaymentBlockData(props.pageBlocks).purpose === PaymentBlockPurpose.Upsell &&
            getPaymentBlockData(props.pageBlocks).upsell_urgency_type === UpsellUrgencyType.Timer
        )) {
            setDisplayDate({
                days: 0,
                hours: 0,
                minutes: 0,
                seconds: 0,
            });
            setSeconds(0);
        }
    }, [props.blockData.timer_type, props.pageBlocks]);

    React.useEffect(() => {
        if(
            seconds &&
            props.blockData.timer_type === CountdownTimerType.Upsell &&
            getPaymentBlockData(props.pageBlocks)&&
            getPaymentBlockData(props.pageBlocks).upsell_urgency_type === UpsellUrgencyType.Timer
        ) {
            const timeout = setTimeout(() => {
                setDisplayDate(convertSeconds(seconds - 1));
                setSeconds(seconds - 1);
            }, MILLISECONDS_IN_A_SECOND);

            return () => clearTimeout(timeout);
        }
    }, [props.blockData.timer_type, seconds, props.pageBlocks]);

    const convertSeconds = (seconds: number) => {
        const days = Math.floor(seconds / (24 * 3600));
        const hours = Math.floor((seconds % (24 * 3600)) / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
      
        return { days, hours, minutes: mins, seconds: secs };
    };

    const isFutureDate = (dateString: string) => {
        // Parse the date string in UTC
        const givenDate = dayjs.utc(dateString);
        
        // Get the current date and time in UTC
        const now = dayjs.utc();
      
        // Compare the given date with the current date
        return givenDate.isAfter(now);
    }

    const timeToFutureDate = (futureDateString: string) => {
        // Parse the future date string in UTC
        const futureDate = dayjs.utc(futureDateString);
        
        // Get the current date in the user's local timezone
        const now = dayjs();
    
        // Calculate the duration between the future date and now in milliseconds
        const diffInMillis = futureDate.diff(now);

        // Convert the total duration into dayjs duration object
        const diff = dayjs.duration(diffInMillis);

        // Extract the parts of the duration
        const days = Math.floor(diff.asDays());
        const hours = diff.hours();
        const minutes = diff.minutes();
        const seconds = diff.seconds();
      
        return {
          days,
          hours,
          minutes,
          seconds
        };
    }

  return (
    <BlockContainer
    design={props.blockData.design}
    >
        <BlockContentContainer
        className={`${props.view ? props.view : ''}`}
        design={props.blockData.design}
        >
            <DisplayDateContainer
            >
                <DisplayDateUnitContainer
                className={`${props.view ? props.view : ''}`}
                >
                    <DisplayDateText
                    design={props.blockData.design}
                    >
                        {displayDate.days}
                    </DisplayDateText>
                    <DisplayDateLabel
                    design={props.blockData.design}
                    >
                        DAYS
                    </DisplayDateLabel>
                </DisplayDateUnitContainer>

                <DisplayDateUnitContainer
                className={`${props.view ? props.view : ''}`}
                >
                    <DisplayDateText
                    design={props.blockData.design}
                    >
                        {displayDate.hours}
                    </DisplayDateText>
                    <DisplayDateLabel
                    design={props.blockData.design}
                    >
                        HOURS
                    </DisplayDateLabel>
                </DisplayDateUnitContainer>

                <DisplayDateUnitContainer
                className={`${props.view ? props.view : ''}`}
                >
                    <DisplayDateText
                    design={props.blockData.design}
                    >
                        {displayDate.minutes}
                    </DisplayDateText>
                    <DisplayDateLabel
                    design={props.blockData.design}
                    >
                        MINUTES
                    </DisplayDateLabel>
                </DisplayDateUnitContainer>

                <DisplayDateUnitContainer
                className={`${props.view ? props.view : ''}`}
                >
                    <DisplayDateText
                    design={props.blockData.design}
                    >
                        {displayDate.seconds}
                    </DisplayDateText>
                    <DisplayDateLabel
                    design={props.blockData.design}
                    >
                        SECONDS
                    </DisplayDateLabel>
                </DisplayDateUnitContainer>
            </DisplayDateContainer>
        </BlockContentContainer>
    </BlockContainer>
  )
}

export default CountdownTimerBlockPreview;